import { useEffect, useMemo, useState } from 'react';
import type { Stage, UseChecklistResult } from '../types';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import { loadBackupFromCloud, saveBackupToCloud, updateBackupInCloud } from '../lib/backupApi';
import {
  clearFamilySyncPrefs,
  loadFamilySyncPrefs,
  markFamilyLinked,
  type FamilySyncPrefs,
} from '../lib/familySync';
import type { BackupPayload } from '../lib/userStore';
import styles from '../App.module.css';

interface SharePanelProps {
  stages: Stage[];
  checklist: UseChecklistResult;
  exportBackup: () => BackupPayload | null;
  onSyncFromBackup: (payload: BackupPayload) => { ok: boolean; message?: string };
  syncPrefs: FamilySyncPrefs | null;
  onSyncPrefsChange: (prefs: FamilySyncPrefs | null) => void;
}

function buildShareText(
  stages: Stage[],
  isChecked: (stageId: string, itemId: string) => boolean,
  locale: string
): string {
  const lines: string[] = [];
  lines.push(
    locale === 'en'
      ? 'Yorisoi Note — family checklist'
      : locale === 'zh'
        ? '依偎笔记 — 家庭清单'
        : 'よりそいノート — 家族のチェックリスト'
  );
  lines.push('');
  for (const stage of stages) {
    const done = stage.checklist.filter((i) => isChecked(stage.id, i.id)).length;
    lines.push(`【${stage.title}】 ${done}/${stage.checklist.length}`);
    for (const item of stage.checklist) {
      const mark = isChecked(stage.id, item.id) ? '✓' : '○';
      lines.push(`${mark} ${item.text}`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

function formatSyncTime(iso: string | undefined, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : locale === 'zh' ? 'zh-CN' : 'ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function SharePanel({
  stages,
  checklist,
  exportBackup,
  onSyncFromBackup,
  syncPrefs,
  onSyncPrefsChange,
}: SharePanelProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  const linked = Boolean(syncPrefs?.autoSync && syncPrefs.code);

  useEffect(() => {
    if (!syncPrefs) {
      const loaded = loadFamilySyncPrefs();
      if (loaded) onSyncPrefsChange(loaded);
    }
  }, [syncPrefs, onSyncPrefsChange]);

  const summary = useMemo(() => {
    let done = 0;
    let total = 0;
    for (const stage of stages) {
      total += stage.checklist.length;
      done += stage.checklist.filter((i) => checklist.isChecked(stage.id, i.id)).length;
    }
    return { done, total };
  }, [stages, checklist]);

  async function copyText(text: string, okMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      setStatus(okMessage);
    } catch {
      setStatus(ui.share.copyFailed);
    }
  }

  async function handleCreateOrUpdate() {
    const payload = exportBackup();
    if (!payload) {
      setStatus(ui.share.syncFailed);
      return;
    }
    setBusy(true);
    setStatus('');
    try {
      const existing = syncPrefs?.code;
      if (existing) {
        const updated = await updateBackupInCloud(existing, payload);
        if (updated.ok) {
          const prefs = markFamilyLinked(existing, 'host');
          onSyncPrefsChange(prefs);
          setStatus(
            locale === 'en'
              ? `Linked. Auto-sync keeps code ${existing} up to date.`
              : locale === 'zh'
                ? `已连接。共享码 ${existing} 会自动保持最新。`
                : `接続済み。共有コード ${existing} を自動で最新に保ちます。`
          );
        } else {
          const created = await saveBackupToCloud(payload);
          if (!created.ok) {
            setStatus(ui.share.syncFailed);
          } else {
            const prefs = markFamilyLinked(created.code, 'host');
            onSyncPrefsChange(prefs);
            setStatus(`${ui.share.codeLabel}: ${created.code}`);
          }
        }
      } else {
        const created = await saveBackupToCloud(payload);
        if (!created.ok) {
          setStatus(ui.share.syncFailed);
        } else {
          const prefs = markFamilyLinked(created.code, 'host');
          onSyncPrefsChange(prefs);
          setStatus(`${ui.share.codeLabel}: ${created.code}`);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSync() {
    setBusy(true);
    setStatus('');
    const loaded = await loadBackupFromCloud(joinCode);
    if (!loaded.ok) {
      setStatus(ui.share.syncFailed);
      setBusy(false);
      return;
    }
    const applied = onSyncFromBackup(loaded.payload);
    if (applied.ok) {
      const code = joinCode.trim().toUpperCase();
      const prefs = markFamilyLinked(code, 'member');
      onSyncPrefsChange(prefs);
      // push merged state back so both sides converge
      const payload = exportBackup();
      if (payload) await updateBackupInCloud(code, payload);
      setStatus(ui.share.syncOk);
      setJoinCode('');
    } else {
      setStatus(ui.share.syncFailed);
    }
    setBusy(false);
  }

  function handleUnlink() {
    clearFamilySyncPrefs();
    onSyncPrefsChange(null);
    setStatus(ui.share.unlinked);
  }

  return (
    <section className={styles.sharePanel} id="family-share">
      <button
        type="button"
        className={styles.shareToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {ui.share.title}
        <span aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className={styles.shareBody}>
          <p className={styles.shareNote}>{ui.share.intro}</p>
          <p className={styles.shareProgressHint}>
            {locale === 'en'
              ? `Current progress: ${summary.done}/${summary.total}`
              : locale === 'zh'
                ? `当前进度：${summary.done}/${summary.total}`
                : `いまの進捗：${summary.done}/${summary.total}`}
          </p>

          {linked ? (
            <div className={styles.shareStep}>
              <p className={styles.shareCodeDisplay} role="status">
                <strong>{ui.share.autoOn}</strong>
                <span className={styles.shareCodeHint}>{ui.share.autoHint}</span>
                <span className={styles.shareCodeHint}>
                  {ui.share.codeLabel}: {syncPrefs!.code}
                </span>
                <span className={styles.shareCodeHint}>
                  {ui.share.lastSync.replace(
                    '{time}',
                    formatSyncTime(syncPrefs?.lastSyncAt, locale)
                  )}
                </span>
              </p>
              <div className={styles.shareActions}>
                <button
                  type="button"
                  className={styles.ghostButton}
                  onClick={() => void copyText(syncPrefs!.code, ui.share.codeCopied)}
                >
                  {ui.share.copyCode}
                </button>
                <button type="button" className={styles.ghostButton} onClick={handleUnlink}>
                  {ui.share.unlink}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.shareStep}>
                <h3 className={styles.shareStepTitle}>{ui.share.forMomTitle}</h3>
                <p className={styles.shareStepBody}>
                  {ui.share.forMomBody} {ui.share.autoHint}
                </p>
                <div className={styles.shareActions}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    disabled={busy}
                    onClick={() => void handleCreateOrUpdate()}
                  >
                    {ui.share.createCode}
                  </button>
                </div>
              </div>

              <div className={styles.shareStep}>
                <h3 className={styles.shareStepTitle}>{ui.share.forFamilyTitle}</h3>
                <p className={styles.shareStepBody}>{ui.share.forFamilyBody}</p>
                <label className={styles.onboardingField}>
                  <span>{ui.share.codeLabel}</span>
                  <input
                    className={styles.onboardingInput}
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder={ui.share.codePlaceholder}
                    autoCapitalize="characters"
                    inputMode="text"
                  />
                </label>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={busy || joinCode.trim().length < 8}
                  onClick={() => void handleSync()}
                >
                  {ui.share.syncButton}
                </button>
              </div>
            </>
          )}

          <div className={styles.shareStep}>
            <p className={styles.shareStepBody}>{ui.share.textHint}</p>
            <button
              type="button"
              className={styles.ghostButton}
              onClick={() =>
                void copyText(
                  buildShareText(stages, checklist.isChecked, locale),
                  ui.share.copiedText
                )
              }
            >
              {ui.share.copyText}
            </button>
          </div>

          {status ? (
            <p className={styles.shareStatus} role="status">
              {status}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
